import {Suspense, lazy} from "react";
import {useSelector} from "react-redux";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import PageSkeleton from "./components/ui/pageSkeleton";
import {PAGE_ROUTES} from "./constants/Routes";
import {RootState} from "./dataStore/store";
import {Layout} from "./pages/Layout";

const LazyNotFound = lazy(() => import("./pages/NotFound"));

const LazyOrganization = lazy(() => import("./pages/Organization"));
const LazyInstances = lazy(() => import("./pages/Instances"));
const LazyCampaigns = lazy(() => import("./pages/Campaigns"));
const LazyLists = lazy(() => import("./pages/Lists"));
const LazyDashboard = lazy(() => import("./pages/Leads"));
const LazyCreateCampaign = lazy(() => import("./pages/CreateCampaign"));
const LazyCampaignDetails = lazy(() => import("./pages/CampaignDetails"));
const LazyListItems = lazy(() => import("./pages/ListItems"));
const LazyChat = lazy(() => import("./pages/ChatControlRoom"));
const LazyNotifications = lazy(() => import("./pages/Notifications"));
const LazyUsers = lazy(() => import("./pages/Users"));
const LazyProfile = lazy(() => import("./pages/Profile"));
const LazyStudio = lazy(() => import("./pages/Dashboard"));
const LazyLogin = lazy(() => import("./pages/Login"));

// Component to handle conditional routing based on user role
const ConditionalIndexRoute = () => {
  const currentUser = useSelector((state: RootState) => state.userState.user);

  if (currentUser?.role === "manager") {
    return <Navigate to={PAGE_ROUTES.CHAT} />;
  }

  return <Navigate to={PAGE_ROUTES.CRM} />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={PAGE_ROUTES.LOGIN}
          element={
            <Suspense fallback={<PageSkeleton />}>
              <LazyLogin />
            </Suspense>
          }
        />
        
        <Route path={PAGE_ROUTES.HOME} element={<Layout />}>
          <Route index element={<ConditionalIndexRoute />} />
          <Route
            path={PAGE_ROUTES.CRM}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyDashboard />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.ORGANIZATIONS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyOrganization />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.CAMPAIGNS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyCampaigns />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.CAMPAIGN_CREATE}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyCreateCampaign />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.CAMPAIGN}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyCampaignDetails />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.INSTANCES}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyInstances />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.INSTANCE}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyInstances />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.LISTS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyLists />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.LIST}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyListItems />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.CHAT}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyChat />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.CHAT_WITH_PARAMS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyChat />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.NOTIFICATIONS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyNotifications />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.USERS}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyUsers />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.PROFILE}
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyProfile />
              </Suspense>
            }
          />
          <Route
            path={PAGE_ROUTES.SUCCESS}
            element={<Navigate to={PAGE_ROUTES.INSTANCES} />}
          />
          <Route
            path={PAGE_ROUTES.FAILED}
            element={<Navigate to={PAGE_ROUTES.INSTANCES} />}
          />
          <Route
          path={PAGE_ROUTES.DASHBOARD}
          element={
            <Suspense fallback={<PageSkeleton />}>
              <LazyStudio />
            </Suspense>
          }
        />
          <Route
            path="*"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LazyNotFound />
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
